import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react'

import {
    compactTheme,
    darkTheme,
    embedTheme,
} from '../themes'

import {EmbedContext} from 'components/EmbedContext'
import {applyEmbedLanguage} from 'embed/utils/language'
import {
    theme as defaultTheme,
} from '../config'
import i18n from 'i18next'
import {useUiState} from '../../hooks'

type AvailableThemesType = {
    [x: string]: string
}

export type ThemeType = typeof defaultTheme | typeof compactTheme | typeof darkTheme | typeof embedTheme

type ThemeProviderProps = {
    theme?: ThemeType
    children: React.ReactElement
}

const html = document.documentElement

export const AvailableThemes: AvailableThemesType = {
    dark: darkTheme.className,
    light: defaultTheme.className, // Stitches' default theme
}

export const ThemeContext = createContext<ThemeType>(defaultTheme)

const getThemeByClassName = (className: string) => Object.keys(AvailableThemes).find((key) => AvailableThemes[key] === className)

export const getActiveTheme = () => {
    const activeTheme = useContext(ThemeContext)
    return getThemeByClassName(activeTheme.className)
}

const ThemeProvider = ({
    theme,
    children,
}: ThemeProviderProps): React.ReactElement => {
    const [currentTheme, setCurrentTheme] = useState<ThemeType>(defaultTheme)

    const ui = useUiState()
    const embed = useContext(EmbedContext)
    const previousLanguage = useRef(ui.language)

    useEffect(() => {
        const previous = previousLanguage.current
        previousLanguage.current = ui.language

        if (!ui.language && !previous) {
            return
        }

        if (ui.language && i18n.language === ui.language) {
            return
        }

        const syncLanguage = async () => {
            if (!ui.language) {
                await i18n.changeLanguage()
            }

            const language = ui.language || i18n.language

            if (embed?.targetOrigin) {
                await applyEmbedLanguage({
                    language,
                    sessionKey: embed.sessionKey,
                    translations: embed.translations,
                }, embed.targetOrigin)
            } else if (ui.language) {
                await i18n.changeLanguage(ui.language)
            }
        }

        syncLanguage().catch((error) => console.warn('Could not change UI language', error))
    }, [ui.language, embed?.sessionKey, embed?.targetOrigin, embed?.translations])

    const setTheme = (newTheme?: ThemeType) => {
        // Get preferred theme from browser settings
        let preferredTheme: ThemeType = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? darkTheme : defaultTheme

        // ...unless browser setting is overridden in userSettings
        switch (ui?.theme) {
            case 'dark':
                preferredTheme = darkTheme
                break
            case 'light':
                preferredTheme = defaultTheme
                break
            default:
        }

        // ...unless theme is explicitly given
        if (newTheme) {
            preferredTheme = newTheme
        }

        html.classList.remove(...Object.values(AvailableThemes))
        html.classList.add(preferredTheme.className)

        // Compact theme is used when set in settings
        if (ui?.chat_density === 'compact') {
            html.classList.add(compactTheme.className)
        } else {
            html.classList.remove(compactTheme.className)
        }

        setCurrentTheme(preferredTheme)
    }

    useEffect(() => {
        if (window.matchMedia) {
            window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => setTheme())
        }

        setTheme()

        return () => {
            if (window.matchMedia) {
                window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', () => setTheme())
            }
        }
    }, [])

    useEffect(() => {
        setTheme(theme)
    }, [theme, ui])

    return <ThemeContext.Provider value={currentTheme}>{children}</ThemeContext.Provider>
}

ThemeProvider.defaultProps = {theme: undefined}

export default ThemeProvider
